import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import DbNavBar from '../DbNavBar';
import DbIcon from '../DbIcon';
import type { DbContentPageProps } from './DbContentPage.types';

// 安全区域 hooks（库用户需要自行安装 react-native-safe-area-context）
let useSafeAreaInsets: () => { top: number; bottom: number };
try {
    const SafeAreaContext = require('react-native-safe-area-context');
    useSafeAreaInsets = SafeAreaContext.useSafeAreaInsets;
} catch {
    // 如果没有安装 react-native-safe-area-context，返回默认值
    useSafeAreaInsets = () => ({ top: 0, bottom: 0 });
}

// 导航 hooks（库用户需要自行安装 @react-navigation/native）
let useNavigation: () => { goBack: () => void } | null;
try {
    const Navigation = require('@react-navigation/native');
    useNavigation = Navigation.useNavigation;
} catch {
    // 如果没有安装 @react-navigation/native，返回 null
    useNavigation = () => null;
}

/**
 * DbContentPage 内容页面容器组件
 *
 * 功能特点：
 * 1. 自动处理底部安全区域
 * 2. 内置 DbNavBar 导航栏，支持所有 DbNavBar 属性
 * 3. 支持 showBack 快速显示返回按钮
 * 4. 支持 showRight 快速显示更多按钮
 * 5. 支持自定义 leftIcon/rightIcon 覆盖默认行为
 * 6. 支持 onBackPress 自定义返回事件
 * 7. 当 backgroundColor="transparent" 时，导航栏绝对定位浮在内容上方（overlay 模式）
 *
 * @example
 * // 基础用法
 * <DbContentPage title="页面标题">
 *   <Text>页面内容</Text>
 * </DbContentPage>
 *
 * @example
 * // 显示返回按钮和更多按钮
 * <DbContentPage title="详情页" showBack showRight>
 *   <Text>详情内容</Text>
 * </DbContentPage>
 *
 * @example
 * // 透明导航栏 overlay 模式（适用于商品详情等页面）
 * <DbContentPage
 *   backgroundColor="transparent"
 *   useLinearGradient
 *   gradientColors={['transparent', 'transparent', 'transparent']}
 *   customContent={<NavBarContent />}
 * >
 *   <ScrollView>...</ScrollView>
 * </DbContentPage>
 */
const DbContentPage: React.FC<DbContentPageProps> = (props) => {
    const {
        // 页面相关
        children,
        bottomSpacingHeight = 0,
        disableSafeAreaBottom = false,
        contentStyle,

        // 导航栏相关
        title,
        showBack = false,
        showRight = false,
        right,
        leftIcon: customLeftIcon,
        rightIcon: customRightIcon,
        onBackPress,
        navHeight = 44,
        onLeftPress,

        // 其他 DbNavBar 属性
        ...navBarRestProps
    } = props;

    // 获取安全区域
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();

    // 判断是否为 overlay 模式（导航栏透明浮在内容上方）
    const isOverlay = navBarRestProps.backgroundColor === 'transparent';

    // 计算底部间距
    const bottomSpacing = disableSafeAreaBottom
        ? bottomSpacingHeight
        : Math.max(insets?.bottom ?? 0, bottomSpacingHeight);

    // 左侧图标处理
    const leftIcon = useMemo(() => {
        // 优先使用自定义 leftIcon
        if (customLeftIcon !== undefined) {
            return customLeftIcon;
        }

        // showBack 为 true 时显示默认返回按钮
        if (showBack) {
            const handlePress = () => {
                if (onBackPress) {
                    onBackPress();
                } else if (onLeftPress) {
                    onLeftPress();
                } else if (navigation) {
                    navigation.goBack();
                }
            };

            return (
                <DbIcon
                    name="arrow-back"
                    family="Ionicons"
                    size={20}
                    color="#fff"
                    onPress={handlePress}
                />
            );
        }

        return null;
    }, [customLeftIcon, showBack, onBackPress, onLeftPress, navigation]);

    // 右侧图标处理
    const rightIcon = useMemo(() => {
        // 优先使用自定义 rightIcon
        if (customRightIcon !== undefined) {
            return customRightIcon;
        }

        // 其次使用 right 属性（兼容旧版本）
        if (right !== undefined) {
            return right;
        }

        // showRight 为 true 时显示默认 more 按钮
        if (showRight) {
            return <DbIcon name="ellipsis-horizontal" family="Ionicons" size={20} color="#fff" />;
        }

        return null;
    }, [customRightIcon, right, showRight]);

    // overlay 模式：导航栏绝对定位，内容从屏幕顶部开始
    if (isOverlay) {
        return (
            <View style={styles.container}>
                {/* 内容层 - 占满全屏 */}
                <View
                    style={[
                        styles.content,
                        { paddingBottom: bottomSpacing },
                        contentStyle,
                    ]}
                >
                    {children}
                </View>

                {/* 导航栏层 - 绝对定位浮在内容上方 */}
                <View style={styles.overlayNavBar} pointerEvents="box-none">
                    <DbNavBar
                        title={title}
                        navHeight={navHeight}
                        leftIcon={leftIcon}
                        rightIcon={rightIcon}
                        titleStyle={styles.defaultTitleStyle}
                        {...navBarRestProps}
                    />
                </View>
            </View>
        );
    }

    // 普通模式：导航栏在内容上方，垂直排列
    return (
        <View style={styles.container}>
            <DbNavBar
                title={title}
                navHeight={navHeight}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                titleStyle={styles.defaultTitleStyle}
                {...navBarRestProps}
            />
            <View
                style={[
                    styles.content,
                    { paddingBottom: bottomSpacing },
                    contentStyle,
                ]}
            >
                {children}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    content: {
        flex: 1,
    },
    overlayNavBar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
    },
    defaultTitleStyle: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '500',
    },
});

export default DbContentPage;

// 导出类型
export type { DbContentPageProps };
